import React, { useEffect, useState, useCallback } from 'react';
import { supabase } from '../supabaseClient';

// Settings-owned editor for the award outbound email templates (brief §5.1 of
// the Complete-brief award-flow rework). Admin-only — mounted inside the
// already isAdmin-gated Admin page. Editing writes a NEW version and flips the
// old one inactive (never mutates in place), so a sent email always traces back
// to the wording that existed then. Every write is audited by a DB trigger.
//
// Only the BODY is editable inside a brief; here an admin can revise the whole
// structure (subject / greeting / body / sign-off) centrally.

const KINDS = [
  { kind: 'award_not_selected', label: 'Not-awarded email', hint: 'Sent to suppliers who quoted but weren’t chosen.' },
  { kind: 'award_won',          label: 'Award-won email',    hint: 'Sent to the supplier who won the brief.' },
];

const TOKENS = ['{contact_first_name}', '{supplier_name}', '{brief_name}', '{awarded_amount}'];

const labelCls = "font-['Metropolis'] text-[10px] font-bold tracking-[0.15em] uppercase text-[#51534A] block mb-1";
const inputCls = "w-full bg-white border border-line rounded-lg text-ink px-3 py-2 font-['Metropolis'] text-sm focus:outline-none focus:border-[#92D400] transition-colors";

const blankFields = () => ({ subject_template: '', greeting_template: '', body_template: '', signoff_template: '' });

export default function EmailTemplatesPanel({ currentUser }) {
  const [rows, setRows] = useState({});      // { kind: row }
  const [drafts, setDrafts] = useState({});  // { kind: {subject,greeting,body,signoff} }
  const [busyKind, setBusyKind] = useState(null);
  const [error, setError] = useState(null);
  const [savedKind, setSavedKind] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    const { data, error: err } = await supabase
      .from('email_templates')
      .select('*')
      .eq('is_active', true);
    if (err) { setError(err.message); setLoaded(true); return; }
    const byKind = {};
    const draftByKind = {};
    for (const r of (data || [])) {
      byKind[r.kind] = r;
      draftByKind[r.kind] = {
        subject_template: r.subject_template || '',
        greeting_template: r.greeting_template || '',
        body_template: r.body_template || '',
        signoff_template: r.signoff_template || '',
      };
    }
    setRows(byKind);
    setDrafts(draftByKind);
    setLoaded(true);
  }, []);

  useEffect(() => { load(); }, [load]);

  const setField = (kind, key, value) => {
    setDrafts(prev => ({ ...prev, [kind]: { ...(prev[kind] || blankFields()), [key]: value } }));
    setSavedKind(null);
  };

  const isDirty = (kind) => {
    const r = rows[kind]; const d = drafts[kind];
    if (!d) return false;
    if (!r) return true;
    return ['subject_template', 'greeting_template', 'body_template', 'signoff_template']
      .some(k => (r[k] || '') !== (d[k] || ''));
  };

  const save = async (kind) => {
    if (busyKind) return;
    const d = drafts[kind];
    if (!d || !d.subject_template.trim() || !d.greeting_template.trim() || !d.body_template.trim()) {
      setError('Subject, greeting and body are all required.'); return;
    }
    setBusyKind(kind);
    setError(null);
    try {
      const current = rows[kind];
      const nextVersion = (current?.version || 0) + 1;
      // Deactivate the current active row first — the DB enforces one active
      // per kind, so the new insert would otherwise collide.
      if (current) {
        const { error: deErr } = await supabase.from('email_templates')
          .update({ is_active: false }).eq('id', current.id);
        if (deErr) throw new Error(deErr.message);
      }
      const { data: inserted, error: insErr } = await supabase.from('email_templates')
        .insert([{
          kind,
          subject_template: d.subject_template.trim(),
          greeting_template: d.greeting_template.trim(),
          body_template: d.body_template.trim(),
          signoff_template: (d.signoff_template || '').trim(),
          version: nextVersion,
          is_active: true,
          updated_by_user_id: currentUser?.id || null,
        }])
        .select('*')
        .single();
      if (insErr) {
        // Re-activate the old row so we don't leave the kind with no active
        // wording if the insert failed.
        if (current) await supabase.from('email_templates').update({ is_active: true }).eq('id', current.id);
        throw new Error(insErr.message);
      }
      setRows(prev => ({ ...prev, [kind]: inserted }));
      setSavedKind(kind);
    } catch (e) {
      setError(e.message || 'Could not save the template.');
    } finally {
      setBusyKind(null);
    }
  };

  return (
    <div className="bg-white border border-line rounded-2xl p-6">
      <div className="mb-4">
        <h2 className="font-['Metropolis'] text-lg font-bold text-ink">Award email templates</h2>
        <p className="font-['Metropolis'] text-[12px] text-[#51534A] mt-1 leading-relaxed max-w-[640px]">
          The standard wording for the award emails. Saving creates a new version — the previous one is kept so any email sent earlier can be traced to its wording. Per-brief edits in the award flow never change these.
        </p>
        <p className="font-['Metropolis'] text-[11px] text-[#51534A] mt-2">
          Tokens: {TOKENS.map((t, i) => (
            <span key={t}><code className="bg-[#F2F4EA] px-1 py-0.5 rounded text-[10px]">{t}</code>{i < TOKENS.length - 1 ? ' ' : ''}</span>
          ))}
        </p>
      </div>

      {error && <p className="font-['Metropolis'] text-sm text-[#E0290A] mb-3">{error}</p>}
      {!loaded && <p className="font-['Metropolis'] text-sm text-[#51534A]">Loading…</p>}

      {loaded && KINDS.map(({ kind, label, hint }) => {
        const d = drafts[kind] || blankFields();
        const version = rows[kind]?.version;
        return (
          <div key={kind} className="border-t border-line pt-5 mt-5 first:border-t-0 first:pt-0 first:mt-0">
            <div className="flex items-center justify-between gap-3 mb-2">
              <div>
                <h3 className="font-['Metropolis'] text-sm font-bold text-ink">{label}</h3>
                <p className="font-['Metropolis'] text-[11px] text-[#51534A]">{hint}</p>
              </div>
              {version != null && (
                <span className="font-['Metropolis'] text-[10px] font-bold uppercase tracking-[0.12em] text-[#51534A] bg-[#F2F4EA] rounded-full px-2 py-1 flex-shrink-0">v{version}</span>
              )}
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className={labelCls}>Subject</label>
                <input className={inputCls} value={d.subject_template} onChange={e => setField(kind, 'subject_template', e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>Greeting</label>
                <input className={inputCls} value={d.greeting_template} onChange={e => setField(kind, 'greeting_template', e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>Sign-off</label>
                <input className={inputCls} value={d.signoff_template} onChange={e => setField(kind, 'signoff_template', e.target.value)} />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Body</label>
                <textarea rows={4} className={`${inputCls} resize-y`} value={d.body_template} onChange={e => setField(kind, 'body_template', e.target.value)} />
              </div>
            </div>
            <div className="flex items-center gap-3 mt-3">
              <button
                type="button"
                onClick={() => save(kind)}
                disabled={busyKind === kind || !isDirty(kind)}
                className="bg-[#92D400] text-[#131313] rounded-xl font-['Metropolis'] text-xs font-bold px-4 py-2 hover:bg-[#a4e810] transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                {busyKind === kind ? 'Saving…' : 'Save new version'}
              </button>
              {savedKind === kind && <span className="font-['Metropolis'] text-[12px] text-fusion-forest font-semibold">Saved ✓</span>}
              {isDirty(kind) && busyKind !== kind && savedKind !== kind && (
                <span className="font-['Metropolis'] text-[11px] text-[#51534A]">Unsaved changes</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
